import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { slide as Menu } from 'react-burger-menu';
import styled, { useTheme } from 'styled-components';

import { XIcon, MenuIcon } from 'src/packages/icons';
import { Button, Text, NavLinkWithDropdown, Grid } from 'src/packages/core';
import { Link, NavLinkWithDropdownProps } from 'src/packages/core/navlink';
import { goToUrl } from 'src/utils/go-to-url';
import { HeaderUser } from '../Header.types';
import { config } from 'src/config';

const styles = {
  bmBurgerButton: {
    display: 'none',
  },
  bmBurgerBars: {
    display: 'none',
  },
  bmBurgerBarsHover: {
    display: 'none',
  },
  bmCrossButton: {
    display: 'none',
  },
  bmMenuWrap: {
    position: 'fixed',
    height: '100%',
    top: '0',
    transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
    zIndex: '9001',
  },
  bmMenu: {
    background: '#0b0b0b',
  },
  bmItemList: {
    height: 'calc(100% - 56px)',
    color: '#b8b7ad',
    margin: '56px 0 0 0',
    paddingTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderTop: '1px solid #252525',
  },
  bmItem: {
    display: 'inline-block',
    outline: 'none',
    border: 'none',
    padding: 16,
    textAlign: 'start',
  },
  bmOverlay: {
    inset: '0 0 0',
    background: '#0b0b0b',
    top: '56px',
    left: '0',
    bottom: '0',
    right: '0',
    opacity: '1',
    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
};

const BurgerMenuWrapper = styled.div`
  display: none;
  cursor: pointer;

  .MuiList-root {
    max-width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    position: relative;

    svg {
      width: 20px;
      height: 20px;
      display: block;
      z-index: 9999;
      position: relative;
    }
  }
`;

const MenuBottomItems = styled.div`
  margin-top: auto;
  width: 100%;
  padding: 24px 16px !important;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface BurgerMenuProps {
  user?: HeaderUser;
  links: (Link | NavLinkWithDropdownProps)[];
  openLanguagesModal: () => void;
  topNoticeBarHeight?: number;
}

export const BurgerMenu = ({ user, links, topNoticeBarHeight }: BurgerMenuProps): ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const disableBodyScroll = () => {
    document.getElementsByTagName('html')[0].style.overflow = 'hidden';
  };

  const enableBodyScroll = () => {
    document.getElementsByTagName('html')[0].style.overflow = 'unset';
  };

  const openHamburgerMenu = () => {
    setIsOpen(true);
    disableBodyScroll();
  };

  const closeHamburgerMenu = () => {
    setIsOpen(false);
    enableBodyScroll();
  };
  return (
    <BurgerMenuWrapper>
      {isOpen ? (
        <div onClick={closeHamburgerMenu}>
          <XIcon size={21} color={theme.colors.palette.white} />
        </div>
      ) : (
        <div onClick={openHamburgerMenu}>
          <MenuIcon size={21} color={theme.colors.palette.white} />
        </div>
      )}
      <Menu
        right
        styles={{
          ...styles,
          bmMenuWrap: {
            ...styles.bmMenuWrap,
            ...(topNoticeBarHeight && { paddingTop: `${topNoticeBarHeight}px` }), // height of coinflare notice bar
          },
        }}
        width={'100%'}
        isOpen={isOpen}
        onOpen={openHamburgerMenu}
        onClose={closeHamburgerMenu}
      >
        {/* non-logged in user */}
        {!user && (
          <>
            <Grid
              gridTemplateColumns={'1fr 1fr'}
              gridTemplateRows={'1fr'}
              width={'100%'}
              gap={2}
              p={2}
            >
              <Button
                variant={'brandDefault'}
                theme={'dark'}
                text={t('button.login')}
                width={'100%'}
                onClick={() => {
                  goToUrl(`${config.coinflareUrl}/login`);
                }}
              />
              <Button
                theme='dark'
                variant={'secondaryDefault'}
                text={t('button.sign_up')}
                width={'100%'}
                onClick={() => {
                  goToUrl(`${config.coinflareUrl}/register`);
                }}
              />
            </Grid>
          </>
        )}

        {/* menu links */}
        {links.map((item, index) => {
          if (item.restricted && (item.restricted(user) || item.restricted())) return null;

          if (item.hasOwnProperty('sublinks')) {
            return (
              <NavLinkWithDropdown
                key={index}
                sublinks={(item as NavLinkWithDropdownProps).sublinks}
                title={t((item as NavLinkWithDropdownProps).title)}
                highlightSublinks={true}
              />
            );
          } else {
            return (
              <li
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  goToUrl((item as Link).url);
                  closeHamburgerMenu();
                }}
              >
                <Text
                  text={t((item as Link).label)}
                  variant={'p2'}
                  color={theme.colors.palette.white}
                />
              </li>
            );
          }
        })}

        {/* language */}
        {/* <Text
          text={t('language')}
          variant={'paragraph2'}
          color={theme.colors.palette.white}
          onClick={() => openLanguagesModal()}
        /> */}

        {/* download app */}
        <MenuBottomItems>
          <Button
            variant={'tertiaryDefault'}
            theme={'dark'}
            text={t('button.download_coinflare_app')}
            width={'100%'}
            onClick={() => goToUrl(`${config.coinflareAppDownloadUrl}`)}
          />
        </MenuBottomItems>
      </Menu>
    </BurgerMenuWrapper>
  );
};
